import type { DocAiDocument, DocAiResponse } from '@/types/api-response/doc-ai-types';
import { useAuthStore } from '@/stores/auth-store';
import { ofetch } from 'ofetch';

const DEFAULT_ENDPOINT = '/nm/nmed/agent/nmed_document_information';
const DEFAULT_BATCH_SIZE = 50;

export interface DocumentClientOptions {
   endpoint?: string;
   signal?: AbortSignal;
}

export interface FetchDocumentsByIdsOptions extends DocumentClientOptions {
   batchSize?: number;
}

/**
 * Fetches a single document by ID, returning the full DocAiDocument or null.
 */
export async function fetchDocumentById(
   id: string,
   options: DocumentClientOptions = {},
): Promise<DocAiDocument | null> {
   const docs = await fetchDocumentsByIds([id], options);
   return docs[0] ?? null;
}

/**
 * Fetches full DocAiDocuments by IDs.
 * Deduplicates IDs, batches into parallel requests, and flattens the results.
 */
export async function fetchDocumentsByIds(
   ids: string[],
   options: FetchDocumentsByIdsOptions = {},
): Promise<DocAiDocument[]> {
   const uniqueIds = [...new Set(ids)];
   if (uniqueIds.length === 0)
      return [];

   const {
      endpoint = DEFAULT_ENDPOINT,
      signal,
      batchSize = DEFAULT_BATCH_SIZE,
   } = options;

   const batches: string[][] = [];
   for (let i = 0; i < uniqueIds.length; i += batchSize) {
      batches.push(uniqueIds.slice(i, i + batchSize));
   }

   const results = await Promise.all(
      batches.map(batch => fetchBatch(batch, endpoint, signal)),
   );

   return results.flat();
}

async function fetchBatch(
   docIds: string[],
   endpoint: string,
   signal?: AbortSignal,
): Promise<DocAiDocument[]> {
   const authStore = useAuthStore();
   const apiKey = authStore.userApiKey?.api_key || '';

   const response = await ofetch<DocAiResponse>(endpoint, {
      baseURL: import.meta.env.VITE_PUBLIC_API_BASE_URL,
      method: 'POST',
      signal,
      body: {
         api_key: apiKey,
         doc_ids: docIds,
         bureau: '',
         doc_type: '',
         year: '',
         year_from: '',
         year_to: '',
         search_fields_list: [],
         search_keys_list: [],
         global_search_key: '',
         first_row: '0',
         number_of_rows: String(docIds.length),
         sort_field: '',
         sort_order: '',
         target_lat: '',
         target_lng: '',
         radius: 0,
      },
      retry: 3,
      retryDelay: 100,
      retryStatusCodes: [408, 429, 504, 500],
   });

   return response.documents;
}
